import { findLastIndex, uniqBy } from "lodash"
import { Articles } from "@tm/data"
import { Article, ArticleListType, CrossSellingModalType, ECrossSellingType, RequestArticleListPayload, Vehicle } from "@tm/models"
import { PartItem } from "@bundles/basket/data/model"
import { isSameArticle, UIAHtmlAttribute } from "@tm/utils"

export function createRecommendedArticlesRequest(
    parts: PartItem[] | undefined,
    articles: Article[],
    vehicle?: Vehicle
): Articles.GetRecommendedArticlesRequest | undefined {
    const basketArticles = parts?.filter((item) => item.vehicleId === vehicle?.id) ?? []

    let recommendedArticlesIn: Articles.RecommendedArticleIn[] = []

    basketArticles.forEach((basketArticle) => {
        if (basketArticle.articleInformation.articleNumber && basketArticle.articleInformation.supplierId) {
            recommendedArticlesIn.push({
                supplierArticleNo: basketArticle.articleInformation.articleNumber,
                supplierId: basketArticle.articleInformation.supplierId,
                productGroupId: basketArticle.articleInformation.productGroupId,
            })
        }
    })

    articles.forEach((article) => {
        if (article.supplierArticleNo && article.supplier?.id) {
            recommendedArticlesIn.unshift({
                supplierArticleNo: article.supplierArticleNo,
                supplierId: article.supplier.id,
                isSelected: article.isSelected,
                productGroupId: article.productGroup.id,
            })
        }
    })

    recommendedArticlesIn = uniqBy(recommendedArticlesIn, (item) => `${item.supplierArticleNo}_${item.supplierId}`)

    if (!recommendedArticlesIn.length) {
        return undefined
    }

    return {
        modelId: vehicle?.tecDocTypeId ?? undefined,
        maxCount: 1,
        articles: recommendedArticlesIn,
    }
}

export const createVehiclePartsRequestPayload = (
    tecDocTypeId: number | undefined,
    productGroupIds: number[],
    crossSellingModalType: CrossSellingModalType
): RequestArticleListPayload => {
    return {
        productGroups: {
            ids: productGroupIds,
        },
        tecDocTypeId,
        forceReload: true,
        inModal: true,
        useNewModal: true,
        extendedAssortment: false,
        crossSellingModalType,
        skipTma: true,
    }
}

export const createUniversalSearchRequestPayload = (query: string, crossSellingModalType: CrossSellingModalType): RequestArticleListPayload => {
    return {
        uniSearch: {
            query,
        },
        forceReload: true,
        inModal: true,
        useNewModal: true,
        extendedAssortment: false,
        crossSellingModalType,
        skipTma: true,
    }
}

export const isCrossSellingEnabledForList = (
    listType: ArticleListType | undefined,
    isAlternativeSearch?: boolean,
    crossSellingModalType?: CrossSellingModalType
) => {
    if (crossSellingModalType) {
        return false
    }

    if (isAlternativeSearch) {
        switch (listType) {
            case "productGroups":
            case "oe":
                return true
            case "vehicle":
            case "universal":
            case "direct":
            case "offers":
            case "pure":
            default:
                return false
        }
    }

    switch (listType) {
        case "vehicle":
        case "universal":
        case "oe":
            return true
        case "direct":
        case "offers":
        case "pure":
        case "productGroups":
        default:
            return false
    }
}

export function getChainKey(crossSellingModalType?: CrossSellingModalType): ECrossSellingType {
    switch (crossSellingModalType) {
        case ECrossSellingType.alsoBoughtProductGroups:
            return ECrossSellingType.alsoBoughtProductGroups

        case ECrossSellingType.recommendedProductGroups:
            return ECrossSellingType.recommendedProductGroups

        default:
            return ECrossSellingType.alsoBoughtArticles
    }
}

/** Adds a list of articles to a specific chain. Requires: "chain" - existing list of articles / "articles" list of new articles to be added */
export function addArticlesToChain(chain: Article[][], articles: Article[]) {
    const newGroup: Article[] = []

    articles?.forEach((article) => {
        const isInChain = chain.some((group) => group.some((item) => isSameArticle(item, article)))
        if (!isInChain) {
            newGroup.push(article)
        }
    })

    if (!newGroup.length) {
        return chain
    }

    return [...chain, newGroup]
}

/** Finds the index of the last selected article from the array, to be used as articleOrder. */
export const getArticleOrderFromList = (articles: Article[], selectedArticles: Article[]) => {
    return findLastIndex(articles, (article: Article) => selectedArticles.some((selectedArticle) => isSameArticle(article, selectedArticle)))
}

export const getUIASelector = (element: UIAHtmlAttribute | null) => {
    if (!element) {
        return
    }
    return Object.entries(element)
        .map(([key, value]) => `[${key}="${CSS.escape(String(value))}"]`)
        .join("")
}

export function getOrderFromElement(element: HTMLElement | null) {
    if (!element) {
        return undefined
    }

    const computedStyle = window.getComputedStyle(element)
    const orderValue = computedStyle.getPropertyValue("order")

    const parsedOrder = parseInt(orderValue)

    return Number.isNaN(parsedOrder) ? undefined : parsedOrder
}
